import React from 'react';
import { IconButton } from '@biq/apex-ui';
import { Icon } from '@iconify/react';
import { Box, SxProps } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { CollapsedArrowIcon, ExpandedArrowIcon } from '../../../../assets';
import { useAttributeList } from '../../contexts/AttributeListContext';
import {
    AttributeDataType,
    AttributeIcons,
    attributeStyles,
    isAttributeErrorState,
    isAttributeObjectGroup,
    possiblyPopulateNodeAttributes,
} from './attribute-helper';
import TextTruncate from 'react-text-truncate';

const AttributeTreeItem = ({
    attribute,
    onNodeSelected,
    setExpandedNodes,
    nodeId,
    shouldHighlight,
    expandedNodeInfoId,
    setExpandedNodeInfoId,
}: any): React.JSX.Element => {
    const { addAttribute, setSelectedAttribute } = useAttributeList();
    const [expandedIconVisible, setExpandedIconVisible] = React.useState(false);

    const isErrorState: boolean = isAttributeErrorState(attribute);
    const displayNodeInformation = expandedNodeInfoId === nodeId;

    const handleClick = (
        event: React.MouseEvent<HTMLElement>,
        attrib: any
    ): void => {
        event.stopPropagation();
        setSelectedAttribute(attrib.id);
    };

    const handleAddClick = (
        event: React.MouseEvent<HTMLElement>,
        attrib: any
    ): void => {
        event.stopPropagation();
        setExpandedNodes((prevExpanded: any) => [...prevExpanded, attrib.id]);
        addAttribute(attribute.id);
    };

    const handleExpandIconClick = (
        event: React.MouseEvent<HTMLElement>,
        attrib: any
    ): void => {
        event.stopPropagation();

        setExpandedIconVisible((prevState) => !prevState);

        // if current id is already open, set it to blank to close it.
        const id: string = expandedNodeInfoId === attrib.id ? '' : attrib.id;
        setExpandedNodeInfoId?.(id);
    };

    const getCustomExpandedIconStyles = (): SxProps => {
        let customStyle: SxProps = attributeStyles.expandIcon;

        if (!expandedIconVisible) {
            customStyle = {
                ...customStyle,
                ...attributeStyles.hiddenExpandedIcon,
            };
        }

        if (displayNodeInformation) {
            customStyle = {
                ...customStyle,
                ...attributeStyles.visibleExpandedIcon,
            };
        }

        if (isAttributeObjectGroup(attribute)) {
            customStyle = {
                ...customStyle,
                ...attributeStyles.objectOnlyExpandedIcon,
            };
        }

        return customStyle;
    };

    const getCustomTreeItemStyles = (): SxProps => {
        let customTreeItemStyle = attributeStyles.treeItem;

        if (shouldHighlight(attribute)) {
            customTreeItemStyle = {
                ...customTreeItemStyle,
                ...attributeStyles.highlightedItemTree,
            };
        }

        if (isErrorState) {
            customTreeItemStyle = {
                ...customTreeItemStyle,
                ...attributeStyles.errorStateItemTree,
            };
        }

        return customTreeItemStyle;
    };

    return (
        <TreeItem
            key={attribute.id}
            label={
                <Box sx={attributeStyles.treeItemMainContainer}>
                    <Box
                        onClick={(e): void => handleClick(e, attribute)}
                        onMouseOver={(): void => setExpandedIconVisible(true)}
                        onMouseOut={(): void => setExpandedIconVisible(false)}
                        onBlur={(): void => setExpandedIconVisible(false)}
                        sx={getCustomTreeItemStyles()}
                    >
                        <Box sx={attributeStyles.treeItemInfoContainer}>
                            <Box sx={attributeStyles.treeItemInnerContainer}>
                                <img
                                    style={attributeStyles.dataTypeImage}
                                    src={AttributeIcons[attribute?.datatype]}
                                    alt={attribute.datatype}
                                />
                                <Box sx={attributeStyles.attributeName}>
                                    {attribute.treeViewDisplayName}
                                </Box>
                            </Box>
                            <Box sx={attributeStyles.treeItemInnerContainer}>
                                <IconButton
                                    size='small'
                                    onClick={(e): void =>
                                        handleExpandIconClick(e, attribute)
                                    }
                                    onMouseOver={(): void =>
                                        setExpandedIconVisible(true)
                                    }
                                    onMouseOut={(): void =>
                                        setExpandedIconVisible(false)
                                    }
                                    sx={getCustomExpandedIconStyles()}
                                >
                                    {displayNodeInformation ? (
                                        <img
                                            src={CollapsedArrowIcon}
                                            alt='collapsed-icon'
                                        />
                                    ) : (
                                        <img
                                            src={ExpandedArrowIcon}
                                            alt='collapsed-icon'
                                        />
                                    )}
                                </IconButton>
                                {isAttributeObjectGroup(attribute) && (
                                    <Box
                                        onClick={(e): void =>
                                            handleAddClick(e, attribute)
                                        }
                                    >
                                        <Icon
                                            style={
                                                attributeStyles.objectAddIcon
                                            }
                                            icon='mdi:plus-circle'
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {displayNodeInformation && (
                            <Box sx={attributeStyles.infoContainer}>
                                <Box sx={attributeStyles.description}>
                                    <b>Description:</b>{' '}
                                    <TextTruncate
                                        line={4}
                                        element='span'
                                        truncateText='...'
                                        text={attribute?.description ?? '--'}
                                    />
                                </Box>
                                <Box sx={attributeStyles.dataType}>
                                    <b>Data Type:</b> {attribute.datatype}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            }
            nodeId={nodeId}
            id={attribute.id}
        >
            {attribute.attributes?.map((a: AttributeDataType) => {
                const updatedAttribute = possiblyPopulateNodeAttributes(a);

                return (
                    <AttributeTreeItem
                        key={updatedAttribute.id}
                        setExpandedNodes={setExpandedNodes}
                        attribute={updatedAttribute}
                        nodeId={updatedAttribute.id}
                        onNodeSelected={(): void =>
                            onNodeSelected(updatedAttribute)
                        }
                        shouldHighlight={shouldHighlight}
                        expandedNodeInfoId={expandedNodeInfoId}
                        setExpandedNodeInfoId={setExpandedNodeInfoId}
                    />
                );
            })}
        </TreeItem>
    );
};

export const MemoizedAttributeTreeItem = React.memo(AttributeTreeItem);
